import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

// API base URL - Use relative path to avoid CORS issues (nginx proxy handles routing)
// In production, nginx proxy will forward /api/ requests to be.feedbeq.com
// IMPORTANT: Always use relative path to go through nginx proxy (no CORS issues)
// Force relative path - ignore VITE_API_BASE_URL if it's an absolute URL
const envBaseUrl = import.meta.env.VITE_API_BASE_URL
const baseUrl = (envBaseUrl && !envBaseUrl.startsWith('http')) ? envBaseUrl : '/api'

// Log API base URL for debugging
console.log('🚀 Feedbeq API Base URL:', baseUrl)
console.log('🌐 All API requests will go to:', baseUrl)

// 🚀 INTERFACES: Updated to match the new API structure
export interface FeedbeqUser {
  userId: string // NEW: User ID field
  username: string
  role: string // NEW: User role field
  bio: string // NEW: User bio field
  message_count: number
  channels: string[]
  overall_score: number
  date_range: {
    start: string
    end: string
  }
  // Calculated fields (added by transformResponse)
  avatar?: string
  daysActive?: number
}

// NEW: User mapping interface for user-ids endpoint
export interface UserMapping {
  userId: string
  username: string
}

export interface UsersResponse {
  success: boolean
  data: {
    users: FeedbeqUser[]
    total_count: number
    processed_count: number
  }
  message: string
  timestamp: string
}

// NEW: User IDs response interface
export interface UserIdsResponse {
  success: boolean
  data: {
    user_mapping: UserMapping[]
    total_count: number
  }
  message: string
  timestamp: string
}

export interface UserMetricsResponse {
  success: boolean
  data: {
    questions_answered_pct: number
    unresolved_3plus_threads: number
    ping_pong_conversations: number
    avg_message_clarity: number
    problems_solved: number
    off_hour_messages: number
    avg_off_hour_response_hours: number
    call_requests: number
    async_resolutions: number
    async_vs_call_ratio: number
    messages_sent: number
    messages_replied_to: number
    sender_vs_responder_ratio: number
    ignored_messages: number
    ignored_messages_pct: number
    avg_time_between_messages_min: number
    unreplied_conversations_4h: number
    avg_response_time_min: number
    median_response_time_min: number
    avg_acknowledgement_time_min: number
    growth: Record<string, number>
    answer_quality_resolution_score: number
    async_discipline_score: number
    communication_balance_score: number
    responsiveness_score: number
    overall_score: number
    performance_level: string
    userId: string // NEW: User ID in response
    username: string // NEW: Username in response
    role: string // NEW: User role in response
    bio: string // NEW: User bio in response
    // Calculated fields (added by transformResponse)
    radarMetrics?: RadarMetric[]
    growthData?: GrowthDataPoint[]
  }
  message: string
  timestamp: string
}

export interface ChannelInfo {
  channel: string
  message_count: number
  unique_users: number
  date_range: {
    start: string
    end: string
  }
  // Calculated fields (added by transformResponse)
  daysActive?: number
  messagesPerUser?: number
  activityLevel?: 'high' | 'medium' | 'low'
}

export interface ChannelsResponse {
  success: boolean
  data: {
    channels: ChannelInfo[]
  }
  message: string
  timestamp: string
}

export interface ChannelUser {
  username: string
  message_count: number
  // Calculated fields (added by transformResponse)
  percentage?: number
  avatar?: string
}

export interface MessageTypes {
  questions: number
  answers: number
  call_requests: number
}

export interface ChannelStatsResponse {
  success: boolean
  data: {
    channel: string
    total_messages: number
    unique_users: number
    date_range_start: string
    date_range_end: string
    avg_response_time_min: number
    most_active_users: ChannelUser[]
    message_types: MessageTypes
    // Calculated fields (added by transformResponse)
    daysActive?: number
    messagesPerDay?: number
    messagesPerUser?: number
    activityLevel?: 'high' | 'medium' | 'low'
    questionAnswerRatio?: number
  }
  message: string
  timestamp: string
}

export interface RadarMetric {
  axis: string
  value: number
}

export interface GrowthDataPoint {
  month: string
  score: number
}

export interface MetricsSummaryResponse {
  success: boolean
  data: {
    overview: {
      total_messages: number
      total_users: number
      total_channels: number
      total_questions: number
      total_answers: number
      date_range: {
        start: string
        end: string
      }
    }
    activity_by_hour: Record<string, number>
    available_metrics: string[]
    top_users: Array<{
      user_id: string
      username: string
      message_count: number
      score?: number
    }>
  }
  message: string
  timestamp: string
}

export interface MetricData {
  id: string
  name: string
  value: number
  unit?: string
  trend?: 'up' | 'down' | 'stable'
}

export interface TrendData {
  month: string
  value: number
}

export interface CoachingSuggestion {
  id: string
  userId: string
  type: 'takeTheBall' | 'passTheBall'
  priority: 'high' | 'medium' | 'low'
  title: string
  description: string
  completed: boolean
}

// 🚀 HELPER FUNCTIONS

const formatMonth = (key: string): string => {
  const [month, year] = key.split('-')
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${monthNames[parseInt(month) - 1]} ${year}`
}

const getChannelActivityLevel = (messagesPerUser: number): 'high' | 'medium' | 'low' => {
  if (messagesPerUser > 600) return 'high'
  if (messagesPerUser > 400) return 'medium'
  return 'low'
}

const generateAvatarFromUsername = (username: string): string => {
  return username.split(' ').map(name => name[0]).join('').toUpperCase()
}

// 🚀 API SLICE
export const feedbeqApi = createApi({
  reducerPath: 'feedbeqApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json')
      return headers
    },
  }),
  tagTypes: ['Users', 'UserMetrics', 'Channels', 'ChannelStats', 'MetricsSummary', 'UserIds'],
  endpoints: (builder) => ({
    
    // 🔥 USERS: Get all users with full details
    getUsers: builder.query<UsersResponse, { limit?: number }>({
      query: ({ limit = 100 } = {}) => `users?limit=${limit}`,
      providesTags: (result) => 
        result 
          ? [
              ...result.data.users.map(({ userId }) => ({ type: 'Users' as const, id: userId })),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
      keepUnusedDataFor: 60, // Cache for 1 minute
      transformResponse: (response: UsersResponse): UsersResponse => {
        if (response.data?.users) {
          const transformedUsers = response.data.users.map(user => ({
            ...user,
            avatar: generateAvatarFromUsername(user.username),
            daysActive: calculateDaysActive(user.date_range.start, user.date_range.end)
          }))
          
          return {
            ...response,
            data: {
              ...response.data,
              users: transformedUsers
            }
          }
        }
        return response
      }
    }),

    // 🆕 NEW: Get user IDs and names only (lightweight endpoint)
    getUserIds: builder.query<UserIdsResponse, void>({
      query: () => 'user-ids',
      providesTags: [{ type: 'UserIds', id: 'LIST' }],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    
    // 🔥 USER METRICS: Updated to use userId instead of username
    getUserMetrics: builder.query<UserMetricsResponse, string>({
      query: (userId) => `user/${encodeURIComponent(userId)}/metrics`,
      providesTags: (_result, _error, userId) => [{ type: 'UserMetrics', id: userId }],
      keepUnusedDataFor: 300, // Cache for 5 minutes
      transformResponse: (response: UserMetricsResponse): UserMetricsResponse => {
        if (response.data) {
          // Transform growth data for chart
          const growthData: GrowthDataPoint[] = response.data.growth 
            ? Object.entries(response.data.growth).map(([month, score]) => ({
                month: formatMonth(month),
                score: score as number
              }))
            : []

          // Transform metrics for radar chart
          const radarMetrics: RadarMetric[] = [
            { axis: 'Answer Quality', value: Math.round(response.data.answer_quality_resolution_score * 100) },
            { axis: 'Async Discipline', value: Math.round(response.data.async_discipline_score * 100) },
            { axis: 'Communication Balance', value: Math.round(response.data.communication_balance_score * 100) },
            { axis: 'Responsiveness', value: Math.round(response.data.responsiveness_score * 100) },
          ]

          return {
            ...response,
            data: {
              ...response.data,
              radarMetrics,
              growthData
            }
          }
        }
        return response
      }
    }),
    
    // 🔥 CHANNELS: Get all channels
    getChannels: builder.query<ChannelsResponse, void>({
      query: () => 'channels',
      providesTags: (result) => 
        result 
          ? [
              ...result.data.channels.map(({ channel }) => ({ type: 'Channels' as const, id: channel })),
              { type: 'Channels', id: 'LIST' },
            ]
          : [{ type: 'Channels', id: 'LIST' }],
      keepUnusedDataFor: 600, // Cache for 10 minutes
      transformResponse: (response: ChannelsResponse): ChannelsResponse => {
        if (response.data?.channels) {
          const transformedChannels = response.data.channels.map(channel => {
            const daysActive = calculateDaysActive(channel.date_range.start, channel.date_range.end)
            const messagesPerUser = channel.message_count / channel.unique_users
            
            return {
              ...channel,
              daysActive,
              messagesPerUser,
              activityLevel: getChannelActivityLevel(messagesPerUser)
            }
          })
          
          return {
            ...response,
            data: {
              ...response.data,
              channels: transformedChannels
            }
          }
        }
        return response
      }
    }),
    
    // 🔥 CHANNEL STATS: Get detailed statistics for a specific channel
    getChannelStats: builder.query<ChannelStatsResponse, string>({
      query: (channel) => `channel/${encodeURIComponent(channel)}/stats`,
      providesTags: (_result, _error, channel) => [{ type: 'ChannelStats', id: channel }],
      keepUnusedDataFor: 60, // Cache for 1 minute only (faster refresh)
      transformResponse: (response: ChannelStatsResponse): ChannelStatsResponse => {
        if (response.data) {
          const daysActive = calculateDaysActive(response.data.date_range_start, response.data.date_range_end)
          const messagesPerDay = daysActive > 0 ? response.data.total_messages / daysActive : 0
          const messagesPerUser = response.data.total_messages / response.data.unique_users
          const activityLevel = getChannelActivityLevel(messagesPerUser)
          
          // Calculate question/answer ratio
          const questionAnswerRatio = response.data.message_types.answers > 0 
            ? response.data.message_types.questions / response.data.message_types.answers 
            : response.data.message_types.questions

          // Enhance most active users with avatars and percentages
          const enhancedUsers = response.data.most_active_users.map(user => ({
            ...user,
            avatar: generateAvatarFromUsername(user.username),
            percentage: Math.round((user.message_count / response.data.total_messages) * 100)
          }))

          return {
            ...response,
            data: {
              ...response.data,
              daysActive,
              messagesPerDay,
              messagesPerUser,
              activityLevel,
              questionAnswerRatio,
              most_active_users: enhancedUsers
            }
          }
        }
        return response
      }
    }),
    
    // 🔥 METRICS SUMMARY: Get summary of all metrics
    getMetricsSummary: builder.query<MetricsSummaryResponse, { months?: number }>({
      query: ({ months = 6 } = {}) => `metrics/summary?months=${months}`,
      providesTags: (_, __, { months = 6 }) => [
        { type: 'MetricsSummary', id: `SUMMARY-${months}` },
        { type: 'MetricsSummary', id: 'LIST' }
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    
    // 🔥 COACHING: Get coaching suggestions
    getCoachingSuggestions: builder.query<CoachingSuggestion[], string>({
      query: (userId) => `coaching/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'UserMetrics', id: userId }],
      keepUnusedDataFor: 180, // Cache for 3 minutes
    }),
    
    // 🔥 COACHING: Update coaching suggestion
    updateCoachingSuggestion: builder.mutation<any, { id: string; completed: boolean }>({
      query: ({ id, completed }) => ({
        url: `coaching/${id}`,
        method: 'PATCH',
        body: { completed }
      }),
      invalidatesTags: (_result, _error, { id: _id }) => [
        { type: 'UserMetrics', id: 'LIST' },
        { type: 'MetricsSummary', id: 'SUMMARY' }
      ]
    }),
    
    // 🔥 TRENDS: Get trend data
    getTrends: builder.query<TrendData[], void>({
      query: () => 'trends',
      providesTags: [{ type: 'MetricsSummary', id: 'TRENDS' }],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    })
  })
})

// Helper function to calculate days active
function calculateDaysActive(startDate: string, endDate: string): number {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

export const {
  useGetUsersQuery,
  useGetUserIdsQuery, // NEW: Hook for user IDs endpoint
  useGetUserMetricsQuery,
  useGetChannelsQuery,
  useGetChannelStatsQuery,
  useGetMetricsSummaryQuery,
  useGetCoachingSuggestionsQuery,
  useUpdateCoachingSuggestionMutation,
  useGetTrendsQuery,
} = feedbeqApi 